import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/Table';
import { financeReportService } from '../../services/financeReport.service';
import { Calendar, ChevronDown, ChevronUp } from 'lucide-react';
import { InvoiceStatus } from '../../types';

export const MonthlyFinanceReportTab: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(currentMonth);
  const [showTripDetails, setShowTripDetails] = useState(false);

  const { data: report, isLoading, error } = useQuery({
    queryKey: ['finance-monthly-report', year, month],
    queryFn: () => financeReportService.getMonthlyReport(year, month),
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-TZ', {
      style: 'currency',
      currency: 'TZS',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getInvoiceStatusVariant = (status?: InvoiceStatus) => {
    if (!status) return 'default';
    switch (status) {
      case InvoiceStatus.PAID:
        return 'success';
      case InvoiceStatus.ISSUED:
        return 'warning';
      case InvoiceStatus.DRAFT:
        return 'info';
      case InvoiceStatus.CANCELLED:
        return 'error';
      default:
        return 'default';
    }
  };

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Input
          label="Year"
          type="number"
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          min={2020}
          max={2050}
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-green"
          >
            {months.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-end">
          <Button onClick={() => {}} className="w-full">
            <Calendar size={16} className="mr-2" />
            View Report
          </Button>
        </div>
      </div>

      {isLoading && (
        <div className="text-center py-8 text-gray-500">Loading monthly report...</div>
      )}

      {error && (
        <div className="text-center py-8 text-error">Failed to load monthly report</div>
      )}

      {report && (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-primary-green to-green-600 p-6 rounded-lg text-white">
            <h2 className="text-2xl font-bold mb-2">{report.monthName} {report.year}</h2>
            <p className="opacity-90">Monthly Financial Report</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Total Trips</p>
              <p className="text-2xl font-bold text-blue-700">{report.totalTrips}</p>
              <p className="text-xs text-gray-500 mt-1">{report.completedTrips} completed</p>
            </div>

            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Total Invoiced</p>
              <p className="text-2xl font-bold text-green-700">{formatCurrency(report.totalInvoiced)}</p>
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Total Paid</p>
              <p className="text-2xl font-bold text-yellow-700">{formatCurrency(report.totalPaid)}</p>
            </div>

            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Outstanding</p>
              <p className="text-2xl font-bold text-purple-700">{formatCurrency(report.totalOutstanding)}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Total Allowances</p>
              <p className="text-xl font-bold">{formatCurrency(report.totalAllowances)}</p>
              <div className="mt-2 text-xs space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-500">Approved:</span>
                  <span className="font-medium">{formatCurrency(report.approvedAllowances)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Pending:</span>
                  <span className="font-medium">{formatCurrency(report.pendingAllowances)}</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
              <p className="text-xl font-bold text-green-600">{formatCurrency(report.totalRevenue)}</p>
            </div>

            <div className="bg-gradient-to-r from-primary-green to-green-600 p-4 rounded-lg text-white">
              <p className="text-sm opacity-90 mb-1">Net Profit</p>
              <p className="text-xl font-bold">{formatCurrency(report.netProfit)}</p>
              <p className="text-xs opacity-75 mt-1">
                {report.totalRevenue > 0
                  ? `${((report.netProfit / report.totalRevenue) * 100).toFixed(1)}% margin`
                  : '0% margin'}
              </p>
            </div>
          </div>

          <div className="border-t pt-6">
            <button
              onClick={() => setShowTripDetails(!showTripDetails)}
              className="flex items-center gap-2 text-lg font-semibold text-gray-800 hover:text-primary-green transition-colors"
            >
              Trip Details ({report.trips.length})
              {showTripDetails ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>

            {showTripDetails && report.trips.length > 0 && (
              <div className="mt-4 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Trip</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Route</TableHead>
                      <TableHead>Driver</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Invoiced</TableHead>
                      <TableHead className="text-right">Paid</TableHead>
                      <TableHead className="text-right">Allowances</TableHead>
                      <TableHead className="text-right">Net Profit</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.trips.map((trip) => (
                      <TableRow key={trip.tripId}>
                        <TableCell className="font-medium">{trip.tripNumber}</TableCell>
                        <TableCell>{format(parseISO(trip.startDate), 'MMM dd')}</TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {trip.origin} → {trip.destination}
                          </div>
                        </TableCell>
                        <TableCell>{trip.driverName}</TableCell>
                        <TableCell>
                          {trip.invoiceStatus ? (
                            <Badge variant={getInvoiceStatusVariant(trip.invoiceStatus)}>
                              {trip.invoiceStatus}
                            </Badge>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency(trip.invoiceAmount)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(trip.totalPayments)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(trip.approvedAllowances)}</TableCell>
                        <TableCell className="text-right">
                          <span className={trip.netProfit > 0 ? 'text-green-600 font-semibold' : 'text-red-600'}>
                            {formatCurrency(trip.netProfit)}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
